import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import {
  DEFAULT_FIELDS,
  DETAIL_ONLY_DEFAULT_FIELDS,
} from '@/features/InvoiceReport/constants';
import { useTransactionProperties } from '@/features/InvoiceReport/Edit/components/Context/API';
import { useInvoiceReportNavigation } from '@/features/InvoiceReport/Edit/components/hooks/useGetInvoiceReportPath';
import { shouldHideTransactionPropertyColumn } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/utils/shouldHideTransactionPropertyColumn';
import type {
  PaymentRequestForm,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

export type UseVisibleTransactionPropertiesOptions = {
  canDisplayTransactionDetail: boolean;
  canDisplayDrExcise: boolean;
};

/**
 * Returns the list of TransactionProperty items that are visible in the
 * InvoiceTransaction table.
 *
 * Unifies the shouldShowProperty + shouldHideTransactionPropertyColumn logic from useColumns so that
 * UtilizePastDetails (drawer display and import) can share the exact same
 * column visibility rules as the main invoice transaction table.
 *
 * hasCustomKey is always derived from the current form's
 * invoiceTransactions, ensuring consistency between the table and the drawer.
 */
export const useVisibleTransactionProperties = (
  opts: UseVisibleTransactionPropertiesOptions
): TransactionProperty[] => {
  const { canDisplayTransactionDetail, canDisplayDrExcise } = opts;

  const transactionProperties = useTransactionProperties();
  const { isApproverRoleFromPath } = useInvoiceReportNavigation();
  const selectedPayee = useSelectedApPayee();
  const { currentOffice } = useLoginInfo();
  const { control } = useFormContext<PaymentRequestForm>();
  const invoiceTransactions = useWatch({
    control,
    name: 'invoiceTransactions',
  });

  const isMoreThanTeamPlan = !!currentOffice?.isMoreThanTeamPlan;

  return useMemo(() => {
    if (!transactionProperties?.length) return [];

    return transactionProperties.filter((property) => {
      // shouldShowProperty: basic field visibility based on report settings
      const basicFilters = [
        {
          condition: !canDisplayTransactionDetail,
          excludedMasterFieldNames: DETAIL_ONLY_DEFAULT_FIELDS,
        },
        {
          condition: !canDisplayDrExcise,
          excludedMasterFieldNames: [
            DEFAULT_FIELDS.TAX_CATEGORY,
            DEFAULT_FIELDS.TAX_AMOUNT,
          ],
        },
        {
          condition: !isApproverRoleFromPath,
          excludedMasterFieldNames: [DEFAULT_FIELDS.INVOICE_KIND],
        },
        {
          condition: !selectedPayee?.is_withholding_tax,
          excludedMasterFieldNames: [
            DEFAULT_FIELDS.WITHHOLDING_TAX_CHECK,
            DEFAULT_FIELDS.WITHHOLDING_TAX_AMOUNT,
          ],
        },
      ];

      if (
        basicFilters.some(
          (filter) =>
            property.type === 'basic' &&
            filter.condition &&
            filter.excludedMasterFieldNames.includes(property.masterFieldName)
        )
      ) {
        return false;
      }

      // shouldHideColumn: plan-based and key-existence visibility
      const hasCustomKey = (invoiceTransactions ?? []).some(
        (field) => property.id in (field.custom_transaction_items ?? {})
      );

      const shouldHide = shouldHideTransactionPropertyColumn(
        property,
        isMoreThanTeamPlan,
        hasCustomKey
      );

      return !shouldHide;
    });
  }, [
    transactionProperties,
    canDisplayTransactionDetail,
    canDisplayDrExcise,
    isApproverRoleFromPath,
    selectedPayee?.is_withholding_tax,
    isMoreThanTeamPlan,
    invoiceTransactions,
  ]);
};
